import React, { useMemo } from 'react';
import { Paper, Tooltip, Typography } from '@mui/material';
import { AppRow } from '../../layout/appRow';
import { ColorPalette } from '../../styles/constants';
import { ProjectAllocations } from '../../models/reports';
import { TaskAlt, ErrorOutline } from '@mui/icons-material';
import { generateProjectLabel } from '../../services/projectService';

const styles = {
  paper: {
    m: 1,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  name: {
    mb: 2,
    color: ColorPalette.RAISIN_BLACK,
    fontWeight: 700,
    size: '20px',
  },
  percenteage: {
    textUnderlineOffset: '3px',
    textDecoration: 'underline',
  },
  allocationText: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
  },
  totalLabel: {
    fontWeight: 700,
    color: ColorPalette.RAISIN_BLACK,
  },
};

interface ProjectAllocationsCardProps {
  projectAllocations: ProjectAllocations;
}

export function ProjectAllocationsCard({
  projectAllocations,
}: ProjectAllocationsCardProps): JSX.Element {
  const projectLabel = useMemo(
    () =>
      generateProjectLabel(
        projectAllocations.project.name,
        projectAllocations.project.code
      ),
    [projectAllocations.project.code, projectAllocations.project.name]
  );

  const allocationsData = useMemo(() => {
    let total = 0;

    const allocations = (
      <>
        {projectAllocations?.employeeProjectAllocations.map((allocation) => {
          total += allocation.hoursOnProject;

          return (
            <AppRow key={`${allocation.employee.id}`}>
              <Typography sx={styles.allocationText}>
                <Typography sx={{ display: 'flex' }} component="span">
                  {allocation.employee.name}{' '}
                  <Tooltip
                    title={`Logged: ${allocation.totalLoggedHours} / ${allocation.totalWorkingHours} h`}
                  >
                    {allocation.totalLoggedHours.toFixed() ===
                    allocation.totalWorkingHours.toFixed() ? (
                      <TaskAlt
                        sx={{ color: ColorPalette.GREEN, marginLeft: '5px' }}
                      />
                    ) : (
                      <ErrorOutline
                        sx={{
                          color: ColorPalette.WARNING_RED,
                          marginLeft: '5px',
                        }}
                      />
                    )}
                  </Tooltip>
                </Typography>

                <span
                  style={styles.percenteage}
                >{`${parseFloat(allocation.hoursOnProject.toFixed(1))}h`}</span>
              </Typography>
            </AppRow>
          );
        })}
      </>
    );

    return { allocations, total: `${parseFloat(total.toFixed(1))}h` };
  }, [projectAllocations]);

  return (
    <Paper sx={styles.paper}>
      <Typography sx={styles.name}>{projectLabel}</Typography>
      {allocationsData?.allocations}

      <AppRow sx={{ mt: 2 }}>
        <Typography sx={styles.allocationText}>
          <span style={styles.totalLabel}>Total:</span>
          <span style={{ ...styles.percenteage, color: ColorPalette.GREEN }}>
            {allocationsData.total}
          </span>
        </Typography>
      </AppRow>
    </Paper>
  );
}
